import { Card, Typography, message } from "antd";
import moment from "moment";
import { useEffect, useMemo, useRef, useState } from "react";
import JwtAxios from "apiServices";
import { PI_REPORT } from "../../../apiServices/API_ENDPOINTS";
import { postData } from "../../../apiServices/common";
import AppContent from "../../../common/AppContent";
import AppPageHeader from "../../../common/PageHeader";
import ListTable from "../../../common/ListTable";
import ExtraContent from "../../Configuration/CommonComponents/ExtraContent";
import FilterComponent from "../../Configuration/CommonComponents/FilterComponent";

const getDefaultDateRange = () => ({
  start_date: moment().subtract(6, "days"),
  end_date: moment(),
});

const PIReport = () => {
  const [refresh, setRefresh] = useState(0);
  const [tableData, setTableData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const lastFilterValuesRef = useRef(null);

  const { Title } = Typography;

  const refetch = () => {
    setRefresh((prev) => prev + 1);
  };

  const exportPIReport = async (filterValues) => {
    if (!filterValues) {
      message.error("Please select Start Date and End Date");
      return;
    }

    const { start_date, end_date } = filterValues || {};

    if (!start_date || !end_date) {
      message.error("Start Date and End Date are required");
      return;
    }

    setLoading(true);

    try {
      const response = await JwtAxios.post(
        PI_REPORT,
        {
          start_date: moment(start_date).format("DD-MM-YYYY"),
          end_date: moment(end_date).format("DD-MM-YYYY"),
          report_type: "document",
        },
        {
          responseType: "arraybuffer",
        },
      );

      if (response && response.data) {
        const outputFilename = `${moment(start_date).format("DD-MM-YYYY")}-${moment(
          end_date,
        ).format("DD-MM-YYYY")}.xlsx`;

        const url = URL.createObjectURL(
          new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          }),
        );
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", outputFilename);
        document.body.appendChild(link);
        link.click();
        link.remove();

        message.success("PI Report Exported Successfully");
        refetch();
      } else {
        message.error("Something went wrong");
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      message.error("Failed to export PI Report");
    } finally {
      setLoading(false);
    }
  };

  const previewFunction = async (filterValues) => {
    if (!filterValues) {
      message.error("Please select Start Date and End Date");
      return;
    }

    const { start_date, end_date } = filterValues || {};

    if (!start_date || !end_date) {
      message.error("Start Date and End Date are required");
      return;
    }

    lastFilterValuesRef.current = filterValues;
    setFetchError(null);
    setLoading(true);

    const bodyData = {
      start_date: moment(start_date).format("DD-MM-YYYY"),
      end_date: moment(end_date).format("DD-MM-YYYY"),
      report_type: "preview",
    };

    try {
      const response = await postData(PI_REPORT, bodyData);

      if (response?.code === 200) {
        setTableData(response.data || []);
      } else {
        setFetchError("Could not load this list.");
        message.error(response?.message || "Something went wrong");
      }
    } catch (error) {
      setFetchError("Could not load this list.");
      // eslint-disable-next-line no-console
      console.error(error);
      message.error("Failed to fetch PI Report");
    } finally {
      setLoading(false);
    }
  };

  // Load the last 7 days on mount so the page never opens blank; the manual
  // guard above still protects the button/preview path.
  useEffect(() => {
    previewFunction(getDefaultDateRange());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tableColumns = [
    {
      title: "Month",
      dataIndex: "month",
      key: "month",
      width: 150,
    },
    {
      title: "Buying Office",
      dataIndex: "buying_office",
      key: "buying_office",
      width: 200,
    },
    {
      title: "Buyer",
      dataIndex: "buyer",
      key: "buyer",
      width: 220,
    },
    {
      title: "Style",
      dataIndex: "style",
      key: "style",
      width: 180,
    },
    {
      title: "LC No",
      dataIndex: "lc_no",
      key: "lc_no",
      width: 180,
    },
    {
      title: "PI No",
      dataIndex: "pi_no",
      key: "pi_no",
      width: 180,
    },
    {
      title: "Sales Contract",
      dataIndex: "sales_contract",
      key: "sales_contract",
      width: 220,
    },
    {
      title: "Supplier",
      dataIndex: "supplier",
      key: "supplier",
      width: 200,
    },
    {
      title: "Merchandiser",
      dataIndex: "merchandiser",
      key: "merchandiser",
      width: 220,
    },
    {
      title: "PI Value",
      dataIndex: "pi_value",
      key: "pi_value",
      width: 140,
      align: "right",
    },
    {
      title: "PI Create Date",
      dataIndex: "create_date",
      key: "create_date",
      width: 200,
    },
    {
      title: "Item Create Date",
      dataIndex: "item_create_date",
      key: "item_create_date",
      width: 200,
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: 150,
    },
    {
      title: "Item",
      dataIndex: "item",
      key: "item",
      width: 260,
    },
    {
      title: "Item Unit",
      dataIndex: "item_unit",
      key: "item_unit",
      width: 120,
      align: "center",
    },
    {
      title: "Goods Description",
      dataIndex: "good_description",
      key: "good_description",
      width: 200,
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      key: "quantity",
      width: 140,
      align: "right",
    },
    {
      title: "Unit Price",
      dataIndex: "unit_price",
      key: "unit_price",
      width: 140,
      align: "right",
    },
    {
      title: "Total Value",
      dataIndex: "total_value",
      key: "total_value",
      width: 160,
      align: "right",
    },
    {
      title: "Nominated",
      dataIndex: "nominated",
      key: "nominated",
      width: 120,
      align: "center",
    },
  ];

  const memoizedCustomFilter = useMemo(
    () => (
      <FilterComponent
        tableData={tableData}
        filterAPI={exportPIReport}
        filterName={"pi_report"}
        searchButtonText={"Export"}
        defaultOpen
        showFilter={showFilter}
        setShowFilter={setShowFilter}
        isPreview={true}
        previewFunction={previewFunction}
        setTableData={setTableData}
      />
    ),
    [showFilter, tableData],
  );

  return (
    <AppContent
      header={
        <AppPageHeader
          extra={
            <ExtraContent
              refresh={refresh}
              setRefresh={setRefresh}
              handleAddItemOpen={exportPIReport}
              hideAddButton={true}
              showFilter={showFilter}
              setShowFilter={setShowFilter}
            />
          }
          title={<Title level={5}>PI Report</Title>}
        />
      }
      content={
        <div className="containt-body">
          <Card>{memoizedCustomFilter}</Card>
          <ListTable
            loading={loading}
            error={fetchError}
            onRetry={() => previewFunction(lastFilterValuesRef.current || getDefaultDateRange())}
            emptyText="No records to show yet."
            dataSource={tableData || []}
            columns={tableColumns.map((col, idx) => ({
              ...col,
              fixed: idx < 6 ? "left" : undefined,
            }))}
            pagination
            scroll={{
              x: 2000,
              y: 500,
            }}
            bordered
            sticky
            rowKey={(record, index) => `${record.lc_no || ""}-${record.pi_no || ""}-${index}`}
          />
        </div>
      }
    />
  );
};

export default PIReport;

